import { useState, useEffect } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FileSpreadsheet, FileText, Clock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
const API = `${BACKEND_URL}/api`;

const VendorAgingReport = ({ user }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/reports/vendor-aging`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch report');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const exportToExcel = () => {
    if (!report) return;
    
    let csv = `Vendor Aging Report\n`;
    csv += `As of: ${report.as_of_date}\n\n`;
    csv += `Vendor,0-7 Days,8-15 Days,16-30 Days,30+ Days,Total,Bills Count\n`;
    
    report.vendors.forEach(v => {
      csv += `"${v.vendor_name}",${v.days_0_7},${v.days_8_15},${v.days_16_30},${v.days_30_plus},${v.total},${v.bills_count}\n`;
    });
    
    csv += `\nTOTAL,${report.totals.days_0_7},${report.totals.days_8_15},${report.totals.days_16_30},${report.totals.days_30_plus},${report.totals.grand_total},\n`;
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `Vendor_Aging_${report.as_of_date}.csv`);
    toast.success('Report downloaded');
  };

  const exportToPDF = () => {
    if (!report) return;
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Vendor Aging Report</title>
        <style>
          @page { size: landscape; margin: 10mm; }
          body { font-family: Arial, sans-serif; font-size: 11px; }
          h1 { text-align: center; color: #dc2626; }
          .date { text-align: center; color: #666; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ddd; padding: 6px 8px; }
          th { background: #dc2626; color: white; }
          .amount { text-align: right; }
          .warning { color: #f59e0b; }
          .danger { color: #dc2626; font-weight: bold; }
          .total-row { background: #fef2f2; font-weight: bold; }
        </style>
      </head>
      <body>
        <h1>Vendor Aging Report</h1>
        <p class="date">As of ${new Date(report.as_of_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
        <table>
          <thead>
            <tr>
              <th>Vendor</th>
              <th class="amount">0-7 Days</th>
              <th class="amount">8-15 Days</th>
              <th class="amount">16-30 Days</th>
              <th class="amount">30+ Days</th>
              <th class="amount">Total</th>
              <th>Bills</th>
            </tr>
          </thead>
          <tbody>
            ${report.vendors.map(v => `
              <tr>
                <td>${v.vendor_name}</td>
                <td class="amount">${formatCurrency(v.days_0_7)}</td>
                <td class="amount ${v.days_8_15 > 0 ? 'warning' : ''}">${formatCurrency(v.days_8_15)}</td>
                <td class="amount ${v.days_16_30 > 0 ? 'warning' : ''}">${formatCurrency(v.days_16_30)}</td>
                <td class="amount ${v.days_30_plus > 0 ? 'danger' : ''}">${formatCurrency(v.days_30_plus)}</td>
                <td class="amount">${formatCurrency(v.total)}</td>
                <td>${v.bills_count}</td>
              </tr>
            `).join('')}
            <tr class="total-row">
              <td>TOTAL</td>
              <td class="amount">${formatCurrency(report.totals.days_0_7)}</td>
              <td class="amount">${formatCurrency(report.totals.days_8_15)}</td>
              <td class="amount">${formatCurrency(report.totals.days_16_30)}</td>
              <td class="amount">${formatCurrency(report.totals.days_30_plus)}</td>
              <td class="amount">${formatCurrency(report.totals.grand_total)}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="vendor-aging-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
            Vendor Aging Report
          </h1>
          <p className="text-gray-500">As of {new Date(report?.as_of_date).toLocaleDateString('en-IN')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportToExcel}>
            <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
            Excel
          </Button>
          <Button variant="outline" onClick={exportToPDF}>
            <FileText className="w-4 h-4 mr-2 text-red-600" />
            PDF
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-gray-500">0-7 Days</p>
            <p className="text-xl font-bold text-green-600">{formatCurrency(report?.totals?.days_0_7)}</p>
          </CardContent>
        </Card>
        <Card className="bg-yellow-50">
          <CardContent className="pt-4">
            <p className="text-xs text-yellow-600">8-15 Days</p>
            <p className="text-xl font-bold text-yellow-700">{formatCurrency(report?.totals?.days_8_15)}</p>
          </CardContent>
        </Card>
        <Card className="bg-orange-50">
          <CardContent className="pt-4">
            <p className="text-xs text-orange-600">16-30 Days</p>
            <p className="text-xl font-bold text-orange-700">{formatCurrency(report?.totals?.days_16_30)}</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-1">
              <AlertTriangle className="w-3 h-3 text-red-600" />
              <p className="text-xs text-red-600">30+ Days</p>
            </div>
            <p className="text-xl font-bold text-red-700">{formatCurrency(report?.totals?.days_30_plus)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-100">
          <CardContent className="pt-4">
            <p className="text-xs text-gray-600">Total Outstanding</p>
            <p className="text-xl font-bold">{formatCurrency(report?.totals?.grand_total)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Vendor Table */}
      <Card>
        <CardContent className="pt-6">
          {report?.vendors?.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No outstanding bills</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vendor</TableHead>
                  <TableHead className="text-right">0-7 Days</TableHead>
                  <TableHead className="text-right">8-15 Days</TableHead>
                  <TableHead className="text-right">16-30 Days</TableHead>
                  <TableHead className="text-right">30+ Days</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-center">Bills</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report?.vendors?.map((vendor) => (
                  <TableRow key={vendor.vendor_id}>
                    <TableCell className="font-medium">{vendor.vendor_name}</TableCell>
                    <TableCell className="text-right text-green-600">
                      {vendor.days_0_7 > 0 ? formatCurrency(vendor.days_0_7) : '-'}
                    </TableCell>
                    <TableCell className="text-right text-yellow-600">
                      {vendor.days_8_15 > 0 ? formatCurrency(vendor.days_8_15) : '-'}
                    </TableCell>
                    <TableCell className="text-right text-orange-600">
                      {vendor.days_16_30 > 0 ? formatCurrency(vendor.days_16_30) : '-'}
                    </TableCell>
                    <TableCell className="text-right text-red-600 font-semibold">
                      {vendor.days_30_plus > 0 ? formatCurrency(vendor.days_30_plus) : '-'}
                    </TableCell>
                    <TableCell className="text-right font-bold">{formatCurrency(vendor.total)}</TableCell>
                    <TableCell className="text-center">{vendor.bills_count}</TableCell>
                  </TableRow>
                ))}
                {/* Total Row */}
                <TableRow className="bg-gray-100 font-bold">
                  <TableCell>TOTAL</TableCell>
                  <TableCell className="text-right text-green-700">{formatCurrency(report?.totals?.days_0_7)}</TableCell>
                  <TableCell className="text-right text-yellow-700">{formatCurrency(report?.totals?.days_8_15)}</TableCell>
                  <TableCell className="text-right text-orange-700">{formatCurrency(report?.totals?.days_16_30)}</TableCell>
                  <TableCell className="text-right text-red-700">{formatCurrency(report?.totals?.days_30_plus)}</TableCell>
                  <TableCell className="text-right text-lg">{formatCurrency(report?.totals?.grand_total)}</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VendorAgingReport;
